import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import * as Slider from '@radix-ui/react-slider';
import { useChessTimer } from '../../context/ChessTimerContext';

function TimeInput({ 
  value, 
 onChange, 
  label 
}: { 
  value: number; 
  onChange: (value: number) => void; 
  label: string;
}) {
  const minutes = Math.floor(value / 60);

  return (
    <div className="flex items-center justify-between mb-4">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</label>
      <div className="flex items-center space-x-2">
        <Slider.Root
          className="relative flex items-center select-none touch-none w-32 h-5"
          value={[minutes]}
          onValueChange={(values) => onChange(values[0] * 60)}
          max={60}
          min={1}
          step={1}
        >
          <Slider.Track className="bg-gray-200 dark:bg-gray-700 relative grow rounded-full h-2">
            <Slider.Range className="absolute bg-indigo-600 dark:bg-indigo-500 rounded-full h-full" />
          </Slider.Track>
          <Slider.Thumb
            className="block w-6 h-6 bg-white border-2 border-indigo-600 dark:border-indigo-500 shadow-lg rounded-full hover:bg-gray-50 focus:outline-none"
          />
        </Slider.Root>
        <span className="text-sm text-gray-600 dark:text-gray-400 min-w-[3ch]">{minutes}m</span>
      </div>
    </div>
  );
}

export function ChessTimerSettings({ onClose }: { onClose: () => void }) {
  const { settings, setDefaultWhiteTime, setDefaultBlackTime, state, resetTimer } = useChessTimer();

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50" onClick={onClose}>
      <div 
        className="absolute right-0 top-0 bottom-0 w-80 bg-white dark:bg-gray-900 shadow-lg grid grid-rows-[auto_1fr_auto]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="flex items-center">
            <Clock className="w-5 h-5 text-gray-700 dark:text-gray-300" />
            <h2 className="ml-2 text-lg font-semibold text-gray-900 dark:text-white">Chess Timer Settings</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
            aria-label="Close settings"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="overflow-y-auto min-h-0 p-4">
          <div className="space-y-4">
            <TimeInput
              label="White Default Time"
              value={settings.defaultWhiteTime}
              onChange={(value) => setDefaultWhiteTime(value)}
            />
            <TimeInput
              label="Black Default Time"
              value={settings.defaultBlackTime}
              onChange={(value) => setDefaultBlackTime(value)}
            />
            
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                Note: Timer counts UP from 0:00. Settings only affect new games.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
